import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect, useCallback } from 'react';
import { standardAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import {
  Plus, Search, Edit2, Trash2, ChevronDown, ChevronUp,
  GripVertical, Save, X, Shield, BookOpen, ListChecks,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const DEFAULT_PHASES = [
  { code: 'gap_analysis', label: 'Phase 1: Gap Analysis & Planning', order: 1, checklist_items: [], task_templates: [] },
  { code: 'application_implementation', label: 'Phase 2: Documentation & NABH Application', order: 2, checklist_items: [], task_templates: [] },
  { code: 'desktop_nc_closure', label: 'Phase 3: Implementation & Desktop NC Closure', order: 3, checklist_items: [], task_templates: [] },
  { code: 'pre_assessment', label: 'Phase 4: On-Site Assessment Planning', order: 4, checklist_items: [], task_templates: [] },
  { code: 'final_nc_closure', label: 'Phase 5: Post-Assessment Support', order: 5, checklist_items: [], task_templates: [] },
];

export const AccreditationStandardsPage = () => {
  usePageTitle('Accreditation Standards');
  const { isAdmin } = useAuth();
  const [standards, setStandards] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({ code: '', label: '', group: '', category: 'new', phases: [...DEFAULT_PHASES] });

  const fetchStandards = useCallback(async () => {
    try {
      const res = await standardAPI.getAll();
      setStandards(res.data);
    } catch { toast.error('Failed to load standards'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStandards(); }, [fetchStandards]);

  const openCreate = () => {
    setEditing(null);
    setForm({ code: '', label: '', group: '', category: 'new', phases: DEFAULT_PHASES.map(p => ({ ...p, id: '', checklist_items: [], task_templates: [] })) });
    setShowDialog(true);
  };

  const openEdit = (std) => {
    setEditing(std);
    setForm({
      code: std.code,
      label: std.label,
      group: std.group || '',
      category: std.category || 'new',
      phases: std.phases || [],
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.code || !form.label) { toast.error('Code and Label are required'); return; }
    try {
      if (editing) {
        await standardAPI.update(editing.id, { label: form.label, group: form.group, category: form.category, phases: form.phases });
        toast.success('Standard updated');
      } else {
        await standardAPI.create(form);
        toast.success('Standard created');
      }
      setShowDialog(false);
      fetchStandards();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (std) => {
    if (!window.confirm(`Delete "${std.label}"? This cannot be undone.`)) return;
    try {
      await standardAPI.delete(std.id);
      toast.success('Standard deleted');
      fetchStandards();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleActive = async (std) => {
    try {
      await standardAPI.update(std.id, { is_active: !std.is_active });
      toast.success(std.is_active ? 'Deactivated' : 'Activated');
      fetchStandards();
    } catch { toast.error('Failed to update'); }
  };

  // Phase management within form
  const addPhase = () => {
    const order = form.phases.length + 1;
    setForm(f => ({
      ...f,
      phases: [...f.phases, { id: '', code: `phase_${order}`, label: `Phase ${order}`, order, checklist_items: [], task_templates: [] }],
    }));
  };

  const removePhase = (idx) => {
    setForm(f => ({
      ...f,
      phases: f.phases.filter((_, i) => i !== idx).map((p, i) => ({ ...p, order: i + 1 })),
    }));
  };

  const updatePhase = (idx, field, value) => {
    setForm(f => ({
      ...f,
      phases: f.phases.map((p, i) => i === idx ? { ...p, [field]: value } : p),
    }));
  };

  const movePhase = (idx, dir) => {
    const newPhases = [...form.phases];
    const target = idx + dir;
    if (target < 0 || target >= newPhases.length) return;
    [newPhases[idx], newPhases[target]] = [newPhases[target], newPhases[idx]];
    setForm(f => ({ ...f, phases: newPhases.map((p, i) => ({ ...p, order: i + 1 })) }));
  };

  const filtered = standards.filter(s =>
    s.label.toLowerCase().includes(search.toLowerCase()) ||
    s.code.toLowerCase().includes(search.toLowerCase()) ||
    (s.group || '').toLowerCase().includes(search.toLowerCase())
  );

  const catColor = (c) => {
    if (c === 'new') return 'bg-emerald-100 text-emerald-700';
    if (c === 'renewal') return 'bg-amber-100 text-amber-700';
    if (c === 'surveillance') return 'bg-sky-100 text-sky-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6" data-testid="accreditation-standards-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-500 rounded-xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Shield className="w-7 h-7" /> Accreditation Standards</h1>
            <p className="text-white/80 text-sm mt-1">Create, modify and manage accreditation standards and their phases</p>
          </div>
          {isAdmin() && (
            <Button onClick={openCreate} className="bg-white text-indigo-700 hover:bg-indigo-50" data-testid="create-standard-btn">
              <Plus className="w-4 h-4 mr-2" /> New Standard
            </Button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input placeholder="Search standards..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="search-standards" />
      </div>

      {/* Standards List */}
      <div className="space-y-3">
        {loading ? (
          <Card><CardContent className="p-8 text-center text-gray-500">Loading...</CardContent></Card>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-gray-500">No standards found</CardContent></Card>
        ) : filtered.map(std => (
          <Card key={std.id} className={`transition-all ${!std.is_active ? 'opacity-60' : ''}`} data-testid={`standard-card-${std.id}`}>
            <CardContent className="p-0">
              {/* Main row */}
              <div className="flex items-center justify-between p-4 cursor-pointer" onClick={() => setExpandedId(expandedId === std.id ? null : std.id)}>
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-gray-900">{std.label}</h3>
                      <Badge className={catColor(std.category)}>{std.category}</Badge>
                      {!std.is_active && <Badge variant="outline" className="text-red-500 border-red-300">Inactive</Badge>}
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">
                      Code: <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">{std.code}</span>
                      {std.group && <span className="ml-3">Group: {std.group}</span>}
                      <span className="ml-3">{std.phases?.length || 0} phases</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isAdmin() && (
                    <>
                      <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); toggleActive(std); }} data-testid={`toggle-active-${std.id}`}>
                        {std.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button variant="ghost" size="icon" onClick={e => { e.stopPropagation(); openEdit(std); }} data-testid={`edit-standard-${std.id}`}>
                        <Edit2 className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={e => { e.stopPropagation(); handleDelete(std); }} data-testid={`delete-standard-${std.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </>
                  )}
                  {expandedId === std.id ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </div>
              </div>

              {/* Expanded phase details */}
              {expandedId === std.id && (
                <div className="border-t px-4 pb-4 pt-3 bg-gray-50/50">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1"><ListChecks className="w-4 h-4" /> Phases</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">#</TableHead>
                        <TableHead>Phase Name</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead className="w-24">Checklists</TableHead>
                        <TableHead className="w-24">Tasks</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(std.phases || []).sort((a, b) => a.order - b.order).map(p => (
                        <TableRow key={p.id || p.code}>
                          <TableCell className="font-mono text-sm">{p.order}</TableCell>
                          <TableCell className="font-medium">{p.label}</TableCell>
                          <TableCell><span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">{p.code}</span></TableCell>
                          <TableCell>{p.checklist_items?.length || 0}</TableCell>
                          <TableCell>{p.task_templates?.length || 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="standard-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Standard' : 'Create New Accreditation Standard'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Code *</label>
                <Input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toLowerCase().replace(/\s+/g, '_') }))} placeholder="e.g. nabl_lab_new" disabled={!!editing} data-testid="standard-code-input" />
              </div>
              <div>
                <label className="text-sm font-medium">Label *</label>
                <Input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} placeholder="e.g. NABL Lab Accreditation - New" data-testid="standard-label-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Group</label>
                <Input value={form.group} onChange={e => setForm(f => ({ ...f, group: e.target.value }))} placeholder="e.g. NABL" />
              </div>
              <div>
                <label className="text-sm font-medium">Category</label>
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="renewal">Renewal</SelectItem>
                    <SelectItem value="surveillance">Surveillance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Phase Editor */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-800">Phases ({form.phases.length})</h3>
                <Button variant="outline" size="sm" onClick={addPhase} data-testid="add-phase-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Phase
                </Button>
              </div>
              <div className="space-y-2">
                {form.phases.map((phase, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg border">
                    <div className="flex flex-col gap-1 mt-1">
                      <button onClick={() => movePhase(idx, -1)} className="text-gray-400 hover:text-gray-600"><ChevronUp className="w-4 h-4" /></button>
                      <GripVertical className="w-4 h-4 text-gray-300" />
                      <button onClick={() => movePhase(idx, 1)} className="text-gray-400 hover:text-gray-600"><ChevronDown className="w-4 h-4" /></button>
                    </div>
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-gray-500">Code</label>
                        <Input value={phase.code} onChange={e => updatePhase(idx, 'code', e.target.value.toLowerCase().replace(/\s+/g, '_'))} className="h-8 text-sm" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Label</label>
                        <Input value={phase.label} onChange={e => updatePhase(idx, 'label', e.target.value)} className="h-8 text-sm" />
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 mt-3 whitespace-nowrap">
                      {phase.checklist_items?.length || 0} checklists, {phase.task_templates?.length || 0} tasks
                    </div>
                    <Button variant="ghost" size="icon" className="mt-1 shrink-0" onClick={() => removePhase(idx)}>
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-standard-btn">
              <Save className="w-4 h-4 mr-2" /> {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccreditationStandardsPage;
